import i18n from 'i18n-js';
import io from 'socket.io-client';

import { FetchProvider, SocketProvider } from '../../providers';

import {
  OVERLAY_ERROR,
  OVERLAY_REQUEST,
  OVERLAY_RESET,
  OVERLAY_SET_AD,
  OVERLAY_SET_ALERT,
  OVERLAY_REMOVE_ALERT,
  OVERLAY_SUCCESS,
  OVERLAY_SET_MATCH_DATA,
} from './types';

export const overlayError = (error) => ({
  type: OVERLAY_ERROR,
  error,
});

export const overlayRequest = {
  type: OVERLAY_REQUEST,
};

export const overlayReset = {
  type: OVERLAY_RESET,
};

export const overlaySetAd = (data) => ({
  type: OVERLAY_SET_AD,
  payload: data,
});

export const overlaySetAlert = (data) => ({
  type: OVERLAY_SET_ALERT,
  payload: data,
});

export const overlayRemoveAlert = (alertId) => ({
  type: OVERLAY_REMOVE_ALERT,
  payload: alertId,
});

export const overlaySuccess = (data) => ({
  type: OVERLAY_SUCCESS,
  payload: data,
});

export const overlaySetMatchData = (data) => ({
  type: OVERLAY_SET_MATCH_DATA,
  payload: data,
});

// Helper function to cleanup OBSFootball connections
export const cleanupObsFootballConnection = () => {
  if (obsFootballSocket) {
    console.log('Cleaning up OBSFootball socket connection');
    obsFootballSocket.disconnect();
    obsFootballSocket = null;
  }
  currentMatchId = null;
};

// Global variable to store OBSFootball socket reference
let obsFootballSocket = null;
let currentMatchId = null;

// Helper function to handle SoccerPro overlay logic
const handleSoccerProOverlay = async (dispatch, matchId) => {
  // Check if matchId has actually changed
  if (currentMatchId === matchId) {
    console.log('Match ID unchanged, skipping handleSoccerProOverlay');
    return;
  }

  console.log('Match ID changed from', currentMatchId, 'to', matchId);
  
  // Disconnect existing socket if matchId changed
  if (obsFootballSocket && currentMatchId !== matchId) {
    console.log('Disconnecting from previous OBSFootball socket');
    obsFootballSocket.disconnect();
    obsFootballSocket = null;
  }

  // Update current matchId
  currentMatchId = matchId;

  // If matchId is null/empty, just disconnect and return
  if (!matchId) {
    dispatch(overlaySetMatchData(null));
    return;
  }

  try {
    // Fetch match data from the matches endpoint
    const matchData = await FetchProvider.fetchGet(`matches/${matchId}`);
    
    if (!matchData) {
      console.warn(`Match data not found for ID: ${matchId}`);
      return;
    }

    // Dispatch match data to the store
    dispatch(overlaySetMatchData(matchData));
    // Check if match starts within 15 minutes
    if (matchData?.event?.startingAt) {
      const timeRemaining = (new Date(matchData.event.startingAt).getTime() - new Date().getTime()) / 1000 / 60;
      
      if (timeRemaining <= 15) {
        // Connect to OBSFootball WebSocket if within 15 minutes
        connectToObsFootballSocket(dispatch, matchData.id || matchId);
      } else {
        // Schedule connection for 15 minutes before match starts
        const delayMs = (timeRemaining - 15) * 60 * 1000;
        
        setTimeout(() => {
          // Double-check that matchId hasn't changed before connecting
          if (currentMatchId === matchId) {
            connectToObsFootballSocket(dispatch, matchData.id || matchId);
          }
        }, delayMs);
      }
    } else {
      // If no startingAt time, connect immediately (for testing/live matches)
      connectToObsFootballSocket(dispatch, matchData.id || matchId);
    }

  } catch (error) {
    console.error('Error fetching SoccerPro match data:', error);
    // Continue without match data - component will use mock data
  }
};

// Helper function to connect to OBSFootball WebSocket
const connectToObsFootballSocket = (dispatch, matchId) => {
  // Disconnect existing socket if any
  if (obsFootballSocket) {
    console.log('Disconnecting existing OBSFootball socket before connecting to new match');
    obsFootballSocket.disconnect();
    obsFootballSocket = null;
  }

  console.log('Connecting to OBSFootball WebSocket for match:', matchId);

  // Get OBSFootball API URL from environment or use default
  const obsFootballApiUrl = process.env.REACT_APP_OBSFOOTBALL_API_URL || 'ws://localhost:3001';
  
  obsFootballSocket = io(obsFootballApiUrl, { 
    reconnection: true, 
    reconnectionAttempts: 5, 
    transports: ['websocket'] 
  });

  obsFootballSocket.on('connect', () => {
    console.log('Connected to OBSFootball WebSocket for match:', matchId);
    obsFootballSocket.emit('join', matchId);
  });

  obsFootballSocket.on('message', (data = {}) => {
    console.log('Socket data', data);
    // Only dispatch if this is still the current match
    if (currentMatchId === matchId) {
      dispatch(overlaySetMatchData(data));
    }
    
    // Disconnect if match has ended
    if (data.result) {
      console.log('Match ended, disconnecting OBSFootball WebSocket');
      obsFootballSocket.disconnect();
      obsFootballSocket = null;
      currentMatchId = null;
    }
  });

  obsFootballSocket.on('disconnect', () => {
    console.log('Disconnected from OBSFootball WebSocket');
  });

  obsFootballSocket.on('error', (error) => {
    console.error('OBSFootball WebSocket error:', error);
  });
};

export const getOverlay = (id) => async (dispatch) => {
  dispatch(overlayRequest);
  try {
    const result = await FetchProvider.fetchGet(`overlay/public/${id}`);

    await dispatch(overlaySuccess(result));

    // Handle SoccerPro overlays with special match data fetching
    if (result.category === 'soccerpro' && result.data?.matchId) {
      await handleSoccerProOverlay(dispatch, result.data.matchId);
    }

    await SocketProvider.connect(result._id);
    SocketProvider.listen('overlayChange', async (data) => {
      await dispatch(overlaySuccess(data));
      
      // Handle SoccerPro overlay changes
      if (data.category === 'soccerpro' && data.data?.matchId) {
        await handleSoccerProOverlay(dispatch, data.data.matchId);
      }
    });
    SocketProvider.listen('builderChange', async (data) => {
      await dispatch(overlaySuccess(data));
      
      // Handle SoccerPro overlay changes
      if (data.category === 'soccerpro' && data.data?.matchId) {
        await handleSoccerProOverlay(dispatch, data.data.matchId);
      }
    });
    SocketProvider.listen('showAd', async (data) => {
      await dispatch(overlaySetAd(data));
      setTimeout(() => {
        dispatch(overlaySetAd(null));
      }, data.adTime * 1000);
    });
    SocketProvider.listen('alert', async (data) => {
      await dispatch(overlaySetAlert(data));
    });
    return;
  } catch (e) {
    if (!e?.response)
      return dispatch(overlayError(i18n.t('common.connectionError')));
    if (e?.response?.status === 400 || e?.response?.status === 404)
      return dispatch(overlayError(i18n.t('screens.builder.notFound')));

    return dispatch(overlayError(i18n.t('screens.builder.notFound')));
  }
};
